import { useEffect, useState } from 'react';
import { Modal } from '@/components/ui/Modal';
import { Avatar } from '@/components/ui/Avatar';
import { Icon } from '@/components/ui/Icon';
import { useSipStore } from '../stores/sipStore';
import { useRingtone } from '../hooks/useRingtone';
import { agentApi, type ScreenPop } from '@/shared/api/agentApi';
import { useLiveCall } from '../stores/liveCall';
import { labelFor } from '@/shared/lib/labels';

/**
 * מסך "שיחה נכנסת" — **המקום היחיד** שבו שיחה נכנסת מוצגת (07/09/2026, דורון:
 * "אני רוצה שזה קודם כל יהיה אחד ומסודר"). קודם הוצגו במקביל שני מסכים: כאן,
 * ובנוסף בלוק צהוב בתוך הסופטפון הצף — שני מקורות אמת לאותה שיחה, עם פרטים
 * שונים. הבלוק בפאנל הוסר; הוא רק מציין ששיחה מצלצלת ומפנה לכאן.
 *
 * המסך מציג את כל מה שהמערכת יודעת על המחייג לפני שעונים: איש הקשר מה-CRM,
 * **הקמפיין והליד** (שם הקמפיין, סטטוס, ניסיון כמה, שיחה חוזרת שנקבעה),
 * המספר שאליו חייג, שיחות אחרונות והערה אחרונה.
 */

/** תוויות סטטוס ליד בקמפיין — אותן מילים שהנציג רואה במסך הלידים. */
const LEAD_STATUS: Record<string, string> = {
  queued: 'ממתין לחיוג',
  calling: 'בחיוג',
  callback: 'שיחה חוזרת',
  done: 'טופל',
  closed: 'נסגר',
  failed: 'נכשל',
  dnc: 'לא לחייג',
};

function relTime(iso?: string | null): string {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const sec = Math.round((Date.now() - t) / 1000);
  if (sec < 60) return 'הרגע';
  if (sec < 3600) return `לפני ${Math.floor(sec / 60)} דק׳`;
  if (sec < 86400) return `לפני ${Math.floor(sec / 3600)} שע׳`;
  return new Date(iso).toLocaleDateString('he-IL', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

function callTime(iso?: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('he-IL', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

/** שורת מידע: תווית מימין, ערך משמאל. */
function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, fontSize: 'var(--t-sm)' }}>
      <span style={{ color: 'var(--muted)', whiteSpace: 'nowrap' }}>{label}</span>
      <span style={{ color: 'var(--text)', fontWeight: 500, textAlign: 'end', minWidth: 0 }}>{children}</span>
    </div>
  );
}

function Panel({ title, tone = 'neutral', children }: { title: string; tone?: 'neutral' | 'accent'; children: React.ReactNode }) {
  const accent = tone === 'accent';
  return (
    <section
      style={{
        background: accent ? 'var(--accent-soft)' : 'var(--surface-2)',
        border: `1px solid ${accent ? 'color-mix(in oklab, var(--accent) 22%, var(--line))' : 'var(--line)'}`,
        borderRadius: 'var(--r-3)',
        padding: '12px 14px',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      <div style={{ fontSize: 'var(--t-xs)', fontWeight: 600, color: accent ? 'var(--accent-ink)' : 'var(--muted)', letterSpacing: '0.03em' }}>{title}</div>
      {children}
    </section>
  );
}

export function IncomingCallModal() {
  const incoming = useSipStore((s) => s.incoming);
  const state = useSipStore((s) => s.state);
  const answer = useSipStore((s) => s.answer);
  const reject = useSipStore((s) => s.reject);
  const [pop, setPop] = useState<ScreenPop | null>(null);

  // שיחה שמצלצלת אצל הנציג לפי ה-PBX אבל לא הגיעה ל-JsSIP בטאב הזה —
  // טלפון שולחני, טאב אחר, או INVITE שהדפדפן פספס. מציגים אותה בכל מקרה:
  // "תמיד נראה שיחות נכנסות". אפשר רק לדחות; לענות — במכשיר שמצלצל.
  const serverIncoming = useLiveCall((s) => s.incoming);
  const serverHangup = useLiveCall((s) => s.hangup);
  const remoteOnly = !incoming && !!serverIncoming;
  const from = incoming?.from ?? serverIncoming?.from ?? '';

  // Ring with sound while the call is incoming.
  useRingtone((state === 'ringing-in' && !!incoming) || remoteOnly);

  // Pull the caller's full CRM + campaign context the moment a call comes in.
  useEffect(() => {
    if (!from) { setPop(null); return; }
    let alive = true;
    void agentApi.screenPop(from).then((p) => { if (alive) setPop(p); }).catch(() => {});
    return () => { alive = false; };
  }, [from]);

  if (!incoming && !serverIncoming) return null;

  const c = pop?.contact;
  const lead = pop?.lead ?? null;
  const displayName = c?.name ?? lead?.name ?? incoming?.displayName ?? serverIncoming?.remote_name ?? null;
  const onReject = remoteOnly ? () => { void serverHangup('incoming'); } : reject;
  const did = serverIncoming?.did;
  const viaCampaign = serverIncoming?.via === 'campaign' || !!lead;
  const fields = Object.entries(c?.fields ?? {}).filter(([, v]) => v != null && v !== '').slice(0, 4);

  return (
    <Modal isOpen onClose={onReject} size="md">
      <div className="stack" style={{ gap: 14, padding: 2 }}>
        {/* מי מתקשר */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 6 }}>
          <div className="ringing" style={{ padding: 4, borderRadius: '50%' }}>
            <Avatar name={displayName ?? from} size={64} />
          </div>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>
            <Icon name="phoneIn" size={12} />
            {remoteOnly ? 'שיחה נכנסת — מצלצלת בטלפון שלך' : 'שיחה נכנסת'}
          </div>
          <h2 style={{ margin: 0, fontSize: 'var(--t-xl)' }}>{displayName ?? 'מספר לא מזוהה'}</h2>
          <div dir="ltr" style={{ color: 'var(--text-2)', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-lg)' }}>{from}</div>

          {/* תגיות מצב: קמפיין, מספר היעד, ספאם */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: 'center' }}>
            {viaCampaign && lead?.campaign_name && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 'var(--t-xs)', fontWeight: 600, background: 'var(--accent-soft)', color: 'var(--accent-ink)', border: '1px solid color-mix(in oklab, var(--accent) 30%, transparent)', borderRadius: 'var(--r-pill)', padding: '3px 10px' }}>
                <Icon name="target" size={11} /> קמפיין: {lead.campaign_name}
              </span>
            )}
            {viaCampaign && !lead?.campaign_name && (
              <span style={{ fontSize: 'var(--t-xs)', background: 'var(--accent-soft)', color: 'var(--accent-ink)', borderRadius: 'var(--r-pill)', padding: '3px 10px' }}>שיחת קמפיין</span>
            )}
            {did && (
              <span style={{ fontSize: 'var(--t-xs)', color: 'var(--text-2)', background: 'var(--surface-2)', borderRadius: 'var(--r-pill)', padding: '3px 10px' }}>
                חייג אל <span dir="ltr" style={{ fontFamily: 'var(--font-mono)' }}>{did}</span>
              </span>
            )}
            {pop?.lookup?.is_spam && (
              <span style={{ fontSize: 'var(--t-xs)', fontWeight: 600, color: 'var(--danger)', background: 'var(--danger-soft)', borderRadius: 'var(--r-pill)', padding: '3px 10px' }}>
                חשד לספאם
              </span>
            )}
          </div>
        </div>

        {/* הליד בקמפיין — למי אני עונה ומה כבר קרה איתו */}
        {lead && (
          <Panel title="הליד בקמפיין" tone="accent">
            {lead.campaign_name && <Row label="קמפיין">{lead.campaign_name}</Row>}
            <Row label="סטטוס">{LEAD_STATUS[lead.status] ?? lead.status}</Row>
            {lead.attempts > 0 && (
              <Row label="ניסיונות חיוג">
                {lead.attempts}
                {lead.last_attempt_at && <span style={{ color: 'var(--muted)', fontWeight: 400 }}> · אחרון {relTime(lead.last_attempt_at)}</span>}
              </Row>
            )}
            {lead.disposition && <Row label="תוצאה אחרונה">{labelFor('disposition', lead.disposition)}</Row>}
            {lead.scheduled_callback_at && (
              <Row label="שיחה חוזרת">
                {new Date(lead.scheduled_callback_at).toLocaleString('he-IL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
              </Row>
            )}
            {lead.notes && (
              <div style={{ fontSize: 'var(--t-sm)', color: 'var(--text-2)', borderTop: '1px solid color-mix(in oklab, var(--accent) 18%, transparent)', paddingTop: 8 }}>
                {lead.notes}
              </div>
            )}
          </Panel>
        )}

        {/* פרטי איש הקשר מה-CRM */}
        {c ? (
          <Panel title="פרטי הלקוח">
            {c.company && <Row label="חברה">{c.company}</Row>}
            {c.email && <Row label="אימייל"><span dir="ltr">{c.email}</span></Row>}
            {fields.map(([k, v]) => <Row key={k} label={k}>{String(v)}</Row>)}
            {c.tags.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                {c.tags.map((tg) => (
                  <span key={tg} style={{ fontSize: 'var(--t-xs)', background: 'var(--surface-3)', color: 'var(--text-2)', borderRadius: 'var(--r-pill)', padding: '2px 9px' }}>{tg}</span>
                ))}
              </div>
            )}
            {c.notes.length > 0 && (
              <div style={{ fontSize: 'var(--t-sm)', color: 'var(--text-2)', borderTop: '1px solid var(--line)', paddingTop: 8 }}>
                <span style={{ color: 'var(--muted)' }}>הערה אחרונה{c.notes[0].created_at ? ` · ${relTime(c.notes[0].created_at)}` : ''}: </span>
                {c.notes[0].body}
              </div>
            )}
          </Panel>
        ) : (
          <div style={{ textAlign: 'center', fontSize: 'var(--t-sm)', color: 'var(--muted)' }}>
            {lead ? 'אין כרטיס לקוח — רק ליד בקמפיין' : 'המספר לא מזוהה במערכת'}
          </div>
        )}

        {/* שיחות אחרונות מול המספר */}
        {pop && pop.recent_calls.length > 0 && (
          <Panel title="שיחות אחרונות">
            {pop.recent_calls.slice(0, 3).map((rc) => (
              <div key={rc.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 'var(--t-sm)' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--text-2)' }}>
                  <Icon name={rc.direction === 'inbound' ? 'phoneIn' : 'phoneOut'} size={12} color="var(--muted)" />
                  {rc.direction === 'inbound' ? 'נכנסת' : 'יוצאת'}
                  <span style={{ color: 'var(--muted)' }}>{callTime(rc.started_at)}</span>
                </span>
                <span style={{ color: 'var(--muted)' }}>
                  {rc.disposition ? labelFor('disposition', rc.disposition) : labelFor('call_status', rc.status)}
                </span>
              </div>
            ))}
          </Panel>
        )}

        {/* מענה / דחייה */}
        <div style={{ display: 'flex', gap: 20, marginTop: 4, justifyContent: 'center', alignItems: 'center' }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
            <button
              type="button"
              onClick={onReject}
              className="btn danger"
              style={{ width: 60, height: 60, borderRadius: '50%', padding: 0 }}
              aria-label="reject"
            >
              <Icon name="hang" size={22} />
            </button>
            <span style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>דחה</span>
          </div>
          {remoteOnly ? (
            <div style={{ alignSelf: 'center', fontSize: 'var(--t-xs)', color: 'var(--muted)', maxWidth: 170, textAlign: 'center' }}>
              לענות — במכשיר שמצלצל
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
              <button
                type="button"
                onClick={answer}
                style={{
                  width: 60, height: 60, borderRadius: '50%', padding: 0,
                  background: 'var(--ok)', color: 'var(--on-accent)', border: 'none',
                  cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                }}
                aria-label="answer"
              >
                <Icon name="phone" size={22} />
              </button>
              <span style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>ענה</span>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
